import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1565C0',       // Deep blue (tooth icon color)
      light: '#1E88E5',
      dark: '#0D47A1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00C9A7',       // Teal/cyan (outer ring)
      light: '#4DD9C0',
      dark: '#00897B',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2ECC71',       // Green (leaf/hand element)
      light: '#4CD787',
      dark: '#1AAB58',
    },
    background: {
      default: '#F8FFFE',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0D1B2A',
      secondary: '#4A5568',
    },
    grey: {
      50: '#F7FFFE',
      100: '#EEF9F6',
    },
  },
  typography: {
    fontFamily: '"Playfair Display", "Georgia", serif',
    h1: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
    },
    body1: {
      fontFamily: '"DM Sans", "Helvetica Neue", sans-serif',
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: '"DM Sans", "Helvetica Neue", sans-serif',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.04em',
    },
    subtitle1: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
    },
    caption: {
      fontFamily: '"DM Sans", sans-serif',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(21,101,192,0.08)',
    '0 4px 16px rgba(21,101,192,0.10)',
    '0 8px 24px rgba(21,101,192,0.12)',
    '0 12px 32px rgba(21,101,192,0.14)',
    '0 16px 40px rgba(21,101,192,0.16)',
    '0 20px 48px rgba(21,101,192,0.18)',
    '0 24px 56px rgba(21,101,192,0.20)',
    '0 28px 64px rgba(0,201,167,0.16)',
    '0 32px 72px rgba(0,201,167,0.18)',
    '0 36px 80px rgba(0,201,167,0.20)',
    '0 40px 88px rgba(0,0,0,0.12)',
    '0 44px 96px rgba(0,0,0,0.14)',
    '0 48px 104px rgba(0,0,0,0.16)',
    '0 52px 112px rgba(0,0,0,0.18)',
    '0 56px 120px rgba(0,0,0,0.20)',
    '0 60px 128px rgba(0,0,0,0.22)',
    '0 64px 136px rgba(0,0,0,0.24)',
    '0 68px 144px rgba(0,0,0,0.26)',
    '0 72px 152px rgba(0,0,0,0.28)',
    '0 76px 160px rgba(0,0,0,0.30)',
    '0 80px 168px rgba(0,0,0,0.32)',
    '0 84px 176px rgba(0,0,0,0.34)',
    '0 88px 184px rgba(0,0,0,0.36)',
    '0 92px 192px rgba(0,0,0,0.38)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          padding: '10px 28px',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)',
          boxShadow: '0 4px 20px rgba(21,101,192,0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
            boxShadow: '0 6px 28px rgba(21,101,192,0.50)',
            transform: 'translateY(-2px)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #00C9A7 0%, #00897B 100%)',
          boxShadow: '0 4px 20px rgba(0,201,167,0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00897B 0%, #00695C 100%)',
            boxShadow: '0 6px 28px rgba(0,201,167,0.50)',
            transform: 'translateY(-2px)',
          },
        },
        outlinedPrimary: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(21,101,192,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 16px 48px rgba(21,101,192,0.18)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 20px rgba(21,101,192,0.10)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1565C0',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1565C0',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export default theme;
